import React, { useState, useEffect, useRef } from 'react';
import QRCode from 'qrcode';
import {
  fetchDevices,
  createDevice,
  updateDevice,
  deleteDevice,
  playWarehouseTone,
  unlockWarehouseAudio,
  isPrintBridgeHostEnabled,
  setPrintBridgeHostEnabled,
  isRemotePrintClientEnabled,
  setRemotePrintClientEnabled,
  getClientDeviceId,
  getClientDeviceName,
  setClientDeviceName,
  getLocalPhoneState,
  sendDeviceHeartbeat,
  DEVICE_ROLES,
} from '../services/deviceService';
import {
  getPrinterConfig,
  setPrinterConfig,
  getPrinterLang,
  setPrinterLang,
  getLabelSize,
  setAutoPrintEnabled,
  testPrint,
  isNetworkAddress,
} from '../services/printerService';
import { SERVER_URL } from '../config/serverConfig';
import { makeTr } from '../services/trLookup';
import ShelfCodesModal from './ShelfCodesModal';

/**
 * 📱 Meine Geräte (Live-Übersicht pro Konto) & 🧭 Geräte-Setup
 *
 * - Im Hauptmenü („📱 Meine Geräte", `setupMode = false`):
 *   Zeigt ausschließlich die **Live-Übersicht** aller Geräte im Konto (egal wo sie sich befinden):
 *     1. 📱 Handys & Tablets im Konto (inkl. Anzeige, welcher Drucker/Scanner mit welchem Handy verbunden ist)
 *     2. 🖨️ Etikettendrucker im Konto (inkl. Anzeige, welches Handy — oder welche beiden Handys gleichzeitig — verbunden sind)
 *     3. 📡 Lager-Scanner (Zebra TC75 & Ringscanner) mit ihrer aktuellen Handy-Zuordnung
 *   Kein doppeltes Einrichtungs-Formular darunter — stattdessen direkter Sprung ins Setup („🧭 Im Setup einrichten").
 *
 * - Im Setup-Assistenten (Schritt 4, `setupMode = true`):
 *   Zeigt das Formular zum Anlegen/Koppeln neuer WLAN-Scanner (Zebra TC75), Bluetooth-Ringscanner,
 *   die automatische WLAN-IP-Suche sowie das Scanner-Testfeld.
 */
export default function DevicesModal({
  onClose,
  colors = {},
  language = 'de',
  setupMode = false,
  onOpenPrinter,
  onOpenSetup,
}) {
  const tr = makeTr(language);
  const dark = !!colors.darkMode;

  const [devices, setDevices] = useState([]);
  const [phones, setPhones] = useState([]);
  const [unbinnedCount, setUnbinnedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Setup-Formular (nur in setupMode sichtbar)
  const [newName, setNewName] = useState('Zebra TC75 Lager');
  const [newType, setNewType] = useState('wlan_scanner');
  const [newRole, setNewRole] = useState('lager_einlagern');
  const [newIp, setNewIp] = useState('');
  const [creating, setCreating] = useState(false);

  // Handy umbenennen
  const [editingPhoneId, setEditingPhoneId] = useState(null);
  const [editingPhoneName, setEditingPhoneName] = useState('');

  // 🔍 WLAN-IP-Suchlauf (im Setup)
  const [showIpScanner, setShowIpScanner] = useState(false);
  const [subnetPrefix, setSubnetPrefix] = useState(() => {
    try {
      const savedAddr = getPrinterConfig()?.address || '';
      const m = savedAddr.match(/^(\d{1,3}\.\d{1,3}\.\d{1,3})\.\d{1,3}/);
      if (m) return m[1];
    } catch { /* ignore */ }
    return '192.168.178';
  });
  const [ipScanning, setIpScanning] = useState(false);
  const [ipScanProgress, setIpScanProgress] = useState(0);
  const [foundIps, setFoundIps] = useState([]);
  const ipScanAbortRef = useRef(false);

  const [selectedDevice, setSelectedDevice] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [copiedKey, setCopiedKey] = useState('');
  const [bridgeHostOn, setBridgeHostOn] = useState(() => isPrintBridgeHostEnabled());
  const [remoteClientOn, setRemoteClientOn] = useState(() => isRemotePrintClientEnabled());
  const [showShelfCodes, setShowShelfCodes] = useState(false);
  const [testingPrint, setTestingPrint] = useState(false);

  // Live-Scanner-Testfeld (im Setup)
  const [testInput, setTestInput] = useState('');
  const [lastTestScan, setLastTestScan] = useState(null);
  const testStartRef = useRef(0);
  const testInputRef = useRef(null);

  const myClientId = getClientDeviceId();
  const apiBase = SERVER_URL || 'https://app.gepaeckannahme.de/kiosk';

  const loadDevices = async (silent = false) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      // Zuerst eigenen Live-Status (Handy + Drucker-Verbindung) an den Server melden
      await sendDeviceHeartbeat();
      const res = await fetchDevices();
      if (res) {
        const scannerList = Array.isArray(res.devices) ? res.devices : [];
        const phoneList = Array.isArray(res.phones) ? res.phones : [];
        setDevices(scannerList);
        setPhones(phoneList);
        setUnbinnedCount(res.unbinnedCount || 0);
        if (setupMode && !selectedDevice && scannerList.length > 0) {
          setSelectedDevice(scannerList[0]);
        }
      }
    } catch (err) {
      if (!silent) {
        setError(err?.message || 'Geräte konnten nicht geladen werden');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    loadDevices(false);
    const interval = setInterval(() => loadDevices(true), 15000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line

  // Echtzeit-Updates bei Scanner-Scans oder Handy-/Drucker-Statusänderungen
  useEffect(() => {
    const onRefresh = () => { loadDevices(true); };
    window.addEventListener('kiosk:device-scan', onRefresh);
    window.addEventListener('kiosk:device-status', onRefresh);
    return () => {
      window.removeEventListener('kiosk:device-scan', onRefresh);
      window.removeEventListener('kiosk:device-status', onRefresh);
    };
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!selectedDevice?.token) {
      setQrDataUrl('');
      return;
    }
    const termUrl = `${apiBase}/devices/terminal/${selectedDevice.token}`;
    QRCode.toDataURL(termUrl, { width: 220, margin: 1 })
      .then((url) => setQrDataUrl(url))
      .catch(() => setQrDataUrl(''));
  }, [selectedDevice, apiBase]);

  // Sicherstellen, dass das aktuelle Handy immer in der Liste erscheint (auch sofort beim Öffnen)
  const effectivePhones = React.useMemo(() => {
    const localState = getLocalPhoneState();
    const list = [...phones];
    const myIdx = list.findIndex((p) => p.clientId === myClientId);
    const localEntry = {
      id: myIdx >= 0 ? list[myIdx].id : 'local_self',
      name: myIdx >= 0 ? list[myIdx].name : localState.name,
      clientId: myClientId,
      platform: localState.platform,
      printer: localState.printer,
      battery: localState.battery || (myIdx >= 0 ? list[myIdx].battery : null),
      bondedDevices: Array.isArray(localState.bondedDevices) && localState.bondedDevices.length > 0
        ? localState.bondedDevices
        : (myIdx >= 0 && Array.isArray(list[myIdx].bondedDevices) ? list[myIdx].bondedDevices : []),
      isOnline: true,
      last_seen_at: new Date().toISOString(),
      last_scan: myIdx >= 0 ? list[myIdx].last_scan : null,
      isCurrentDevice: true,
    };
    if (myIdx >= 0) {
      list[myIdx] = { ...list[myIdx], ...localEntry };
    } else {
      list.unshift(localEntry);
    }
    // Eigenes Gerät immer ganz oben anzeigen
    return list.sort((a, b) => {
      if (a.clientId === myClientId) return -1;
      if (b.clientId === myClientId) return 1;
      if (a.isOnline !== b.isOnline) return a.isOnline ? -1 : 1;
      return 0;
    });
  }, [phones, myClientId, bridgeHostOn, remoteClientOn]);

  // 🖨️ Alle aktiven Etikettendrucker im Konto zusammenführen & ermitteln, welche Handys damit verbunden sind
  const accountPrinters = React.useMemo(() => {
    const map = new Map();

    // 1. Zuerst alle direkt verbundenen Drucker (Bluetooth oder WLAN) einsammeln
    for (const ph of effectivePhones) {
      const pr = ph.printer;
      if (!pr?.connected || pr.remoteClient || !pr.address || pr.address === 'remote-bridge') continue;
      const key = String(pr.address).trim().toUpperCase();
      // Prüfen, ob in den gekoppelten Bluetooth-Geräten dieses Handys ein Akkustand für den Drucker vorliegt
      const bondedMatch = Array.isArray(ph.bondedDevices)
        ? ph.bondedDevices.find((bd) => bd.address && String(bd.address).trim().toUpperCase() === key)
        : null;
      const printerBattery = pr.battery || (bondedMatch && Number.isFinite(Number(bondedMatch.batteryLevel))
        ? { level: Number(bondedMatch.batteryLevel) }
        : null);

      if (!map.has(key)) {
        map.set(key, {
          key,
          name: pr.name || pr.address,
          address: pr.address,
          mode: pr.mode || (isNetworkAddress(pr.address) ? 'wifi' : 'bluetooth'),
          lang: pr.lang || 'cpcl',
          size: pr.size || '100x150',
          battery: printerBattery,
          bridgeHostActive: Boolean(pr.bridgeHost),
          connectedPhones: [],
        });
      }
      const entry = map.get(key);
      if (pr.bridgeHost) entry.bridgeHostActive = true;
      if (!entry.battery && printerBattery) entry.battery = printerBattery;
      entry.connectedPhones.push({
        clientId: ph.clientId,
        name: ph.name,
        isCurrentDevice: ph.clientId === myClientId,
        isOnline: ph.isOnline,
        connectionKind: 'direct',
      });
    }

    // 2. Jetzt alle Handys zuordnen, die per "Gleichzeitig mitdrucken" (Remote-Bridge) verbunden sind
    const allDirectPrinters = Array.from(map.values());
    for (const ph of effectivePhones) {
      const pr = ph.printer;
      if (!pr?.connected || !pr.remoteClient) continue;
      const targetAddr = String(pr.address || '').trim().toUpperCase();
      let targetPrinter = map.get(targetAddr) || allDirectPrinters.find((p) => p.bridgeHostActive) || allDirectPrinters[0];
      if (!targetPrinter) {
        const fallbackKey = 'REMOTE_BRIDGE';
        if (!map.has(fallbackKey)) {
          map.set(fallbackKey, {
            key: fallbackKey,
            name: pr.name || 'Shop-Etikettendrucker',
            address: 'remote-bridge',
            mode: 'remote_bridge',
            lang: pr.lang || 'cpcl',
            size: pr.size || '100x150',
            bridgeHostActive: true,
            connectedPhones: [],
          });
        }
        targetPrinter = map.get(fallbackKey);
      }
      if (!targetPrinter.connectedPhones.some((cp) => cp.clientId === ph.clientId)) {
        targetPrinter.connectedPhones.push({
          clientId: ph.clientId,
          name: ph.name,
          isCurrentDevice: ph.clientId === myClientId,
          isOnline: ph.isOnline,
          connectionKind: 'bridge',
        });
      }
    }

    return Array.from(map.values());
  }, [effectivePhones, myClientId]);

  // Dieses Handy mit einem bereits im Konto vorhandenen Drucker gleichzeitig verbinden
  const handleConnectThisPhoneToPrinter = async (printer) => {
    try {
      if (printer.mode === 'wifi' && printer.address && printer.address !== 'remote-bridge') {
        // WLAN-Drucker: Beide Handys können direkt per IP:9100 drucken
        setPrinterConfig({
          name: printer.name,
          address: printer.address,
          type: 'tcp',
        });
        setPrinterLang(printer.lang || 'zpl');
        setAutoPrintEnabled(true);
        setRemoteClientOn(false);
        setRemotePrintClientEnabled(false);
      } else {
        // Bluetooth-Drucker an anderem Handy: Dieses Handy druckt gleichzeitig über die Druck-Brücke mit
        setRemotePrintClientEnabled(true, { name: printer.name, address: printer.address });
        setRemoteClientOn(true);
        setAutoPrintEnabled(true);
      }
      await sendDeviceHeartbeat();
      await loadDevices(true);
      if (window.showToast) {
        window.showToast(`🖨️ Dieses Handy ist jetzt gleichzeitig mit "${printer.name}" verbunden!`, 'success', 3500);
      }
    } catch { /* ignore */ }
  };

  const handleDisconnectThisPhoneFromBridge = async () => {
    setRemotePrintClientEnabled(false);
    setRemoteClientOn(false);
    await sendDeviceHeartbeat();
    await loadDevices(true);
  };

  const handleSavePhoneName = async (phone) => {
    const clean = editingPhoneName.trim();
    if (!clean) {
      setEditingPhoneId(null);
      return;
    }
    setEditingPhoneId(null);
    if (phone.clientId === myClientId) {
      setClientDeviceName(clean);
      await sendDeviceHeartbeat({ customName: clean });
    } else if (phone.id && phone.id !== 'local_self') {
      await updateDevice(phone.id, { name: clean });
    }
    await loadDevices(true);
  };

  const handleAssignScannerToPhone = async (scannerDev, targetClientId) => {
    if (!scannerDev?.id) return;
    const targetPhone = effectivePhones.find((p) => p.clientId === targetClientId);
    const nextConfig = {
      ...(scannerDev.config || {}),
      connectedPhoneClientId: targetClientId || '',
      connectedPhoneName: targetPhone ? targetPhone.name : '',
    };
    try {
      const res = await updateDevice(scannerDev.id, { config: nextConfig });
      if (res?.device) {
        setDevices((prev) => prev.map((d) => (d.id === scannerDev.id ? res.device : d)));
      }
    } catch (err) {
      setError(err?.message || 'Zuordnung konnte nicht gespeichert werden');
    }
  };

  const handleAddDevice = async (e) => {
    e.preventDefault();
    if (!newName.trim() || creating) return;
    setCreating(true);
    setError('');
    try {
      const res = await createDevice({
        name: newName.trim(),
        type: newType,
        role: newRole,
        config: {
          ...(newIp.trim() ? { ip: newIp.trim() } : {}),
          ...(newType === 'bt_scanner' ? { connectedPhoneClientId: myClientId, connectedPhoneName: getClientDeviceName() } : {}),
        },
      });
      if (res?.device) {
        setDevices((prev) => [...prev, res.device]);
        setSelectedDevice(res.device);
        setNewName(newType === 'bt_scanner' ? 'Ringscanner Lager' : 'Zebra TC75 #2');
        setNewIp('');
      }
    } catch (err) {
      setError(err?.message || 'Gerät konnte nicht angelegt werden');
    } finally {
      setCreating(false);
    }
  };

  const handleRoleChange = async (dev, nextRole) => {
    if (!dev?.id || !nextRole) return;
    try {
      const res = await updateDevice(dev.id, { role: nextRole });
      if (res?.device) {
        setDevices((prev) => prev.map((d) => (d.id === dev.id ? res.device : d)));
        if (selectedDevice?.id === dev.id) setSelectedDevice(res.device);
      }
    } catch (err) {
      setError(err?.message || 'Aufgabe konnte nicht geändert werden');
    }
  };

  const handleRemoveDevice = async (dev) => {
    if (!dev?.id || dev.id === 'local_self') return;
    if (!window.confirm(tr(
      `Gerät "${dev.name}" wirklich aus dem Konto entfernen?`,
      `Really remove "${dev.name}" from account?`,
      `Apparaat "${dev.name}" echt verwijderen?`
    ))) return;
    try {
      await deleteDevice(dev.id);
      if (dev.type === 'phone') {
        setPhones((prev) => prev.filter((p) => p.id !== dev.id));
      } else {
        const next = devices.filter((d) => d.id !== dev.id);
        setDevices(next);
        if (selectedDevice?.id === dev.id) {
          setSelectedDevice(next[0] || null);
        }
      }
    } catch (err) {
      setError(err?.message || 'Löschen fehlgeschlagen');
    }
  };

  const copyText = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(''), 2500);
    } catch { /* ignore */ }
  };

  // 🔍 Automatischer Subnetz-Scanner im lokalen WLAN (im Setup)
  const detectLocalSubnetViaWebRTC = async () => {
    try {
      const RTCPeer = window.RTCPeerConnection || window.webkitRTCPeerConnection;
      if (!RTCPeer) return null;
      const pc = new RTCPeer({ iceServers: [] });
      pc.createDataChannel('');
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      return await new Promise((resolve) => {
        const timeout = setTimeout(() => { try { pc.close(); } catch {} resolve(null); }, 900);
        pc.onicecandidate = (ev) => {
          const cand = ev?.candidate?.candidate || '';
          const m = cand.match(/\b((?:192\.168|10\.\d{1,3}|172\.(?:1[6-9]|2\d|3[01]))\.\d{1,3})\.\d{1,3}\b/);
          if (m) {
            clearTimeout(timeout);
            try { pc.close(); } catch {}
            resolve(m[1]);
          }
        };
      });
    } catch {
      return null;
    }
  };

  const probeHostIp = async (ip) => {
    const probePort = async (port, timeoutMs) => {
      const ctrl = new AbortController();
      const t0 = performance.now();
      const timer = setTimeout(() => ctrl.abort(), timeoutMs);
      try {
        await fetch(`http://${ip}:${port}/`, { mode: 'no-cors', cache: 'no-store', signal: ctrl.signal });
        clearTimeout(timer);
        return { alive: true, port, open: true, ms: Math.round(performance.now() - t0) };
      } catch (err) {
        clearTimeout(timer);
        const dt = Math.round(performance.now() - t0);
        if (err?.name !== 'AbortError' && dt < 160) {
          return { alive: true, port, open: false, ms: dt };
        }
        return { alive: false, port, open: false, ms: dt };
      }
    };

    const p9100 = await probePort(9100, 480);
    if (p9100.open) {
      return { ip, kind: 'printer', label: '🖨️ Port 9100 offen (Etikettendrucker / Zebra)', ms: p9100.ms };
    }
    const p80 = await probePort(80, 480);
    if (p80.open) {
      return { ip, kind: 'http', label: '🌐 Web-Interface (Router / Drucker / Gerät)', ms: p80.ms };
    }
    if (p9100.alive || p80.alive) {
      return { ip, kind: 'device', label: '📡 Aktives WLAN-Gerät (Scanner / Handy / PC)', ms: Math.min(p9100.ms, p80.ms) };
    }
    return null;
  };

  const runSubnetIpScan = async (customPrefix) => {
    if (ipScanning) {
      ipScanAbortRef.current = true;
      setIpScanning(false);
      return;
    }
    let prefix = String(customPrefix || subnetPrefix || '').trim().replace(/\.\s*$/, '');
    if (!/^\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(prefix)) {
      const detected = await detectLocalSubnetViaWebRTC();
      prefix = detected || '192.168.178';
      setSubnetPrefix(prefix);
    }
    ipScanAbortRef.current = false;
    setIpScanning(true);
    setIpScanProgress(0);
    setFoundIps([]);

    const hosts = [];
    for (let i = 1; i <= 254; i++) hosts.push(`${prefix}.${i}`);

    const batchSize = 24;
    for (let idx = 0; idx < hosts.length; idx += batchSize) {
      if (ipScanAbortRef.current) break;
      const chunk = hosts.slice(idx, idx + batchSize);
      const results = await Promise.all(chunk.map((ip) => probeHostIp(ip)));
      const hits = results.filter(Boolean);
      if (hits.length > 0) {
        setFoundIps((prev) => {
          const seen = new Set(prev.map((x) => x.ip));
          return [...prev, ...hits.filter((h) => !seen.has(h.ip))];
        });
      }
      setIpScanProgress(Math.min(100, Math.round(((idx + chunk.length) / hosts.length) * 100)));
    }
    setIpScanning(false);
  };

  const cardStyle = {
    padding: '14px 16px',
    borderRadius: 12,
    border: `1px solid ${dark ? '#334155' : '#e2e8f0'}`,
    background: dark ? '#1b202b' : '#f8fafc',
  };

  const formatLastSeen = (iso) => {
    if (!iso) return tr('Noch keine Aktivität', 'No activity yet', 'Nog geen activiteit');
    const diffSec = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    if (diffSec < 60) return tr('gerade eben', 'just now', 'zojuist');
    if (diffSec < 3600) return tr(`vor ${Math.floor(diffSec / 60)} Min.`, `${Math.floor(diffSec / 60)}m ago`, `${Math.floor(diffSec / 60)} min geleden`);
    return new Date(iso).toLocaleString([], { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  // 🔋 Batterieanzeige für Handys, Tablets, Zebra TC75, Ringscanner & mobile Drucker (sofern verfügbar)
  const renderBatteryBadge = (battery) => {
    if (!battery || typeof battery !== 'object') return null;
    const lvl = Number(battery.level);
    if (!Number.isFinite(lvl) || lvl < 0 || lvl > 100) return null;
    const pct = Math.round(lvl);
    const charging = Boolean(battery.charging);
    const isCritical = pct <= 15 && !charging;
    const isLow = pct > 15 && pct <= 30 && !charging;
    const icon = charging ? '⚡' : isCritical ? '🪫' : '🔋';

    const bg = isCritical
      ? (dark ? '#7f1d1d' : '#fee2e2')
      : isLow
        ? (dark ? '#78350f' : '#fef3c7')
        : (dark ? '#064e3b' : '#dcfce7');
    const fg = isCritical
      ? (dark ? '#fca5a5' : '#991b1b')
      : isLow
        ? (dark ? '#fde68a' : '#92400e')
        : (dark ? '#6ee7b7' : '#166534');

    return (
      <span
        data-testid="device-battery-badge"
        title={charging
          ? tr(`Akku: ${pct}% (lädt)`, `Battery: ${pct}% (charging)`, `Accu: ${pct}% (laadt op)`)
          : tr(`Akku: ${pct}%`, `Battery: ${pct}%`, `Accu: ${pct}%`)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 3,
          fontSize: 11.5,
          fontWeight: 800,
          padding: '2px 8px',
          borderRadius: 999,
          background: bg,
          color: fg,
        }}
      >
        <span>{icon}</span>
        <span>{pct}%</span>
      </span>
    );
  };

  return (
    <div
      data-testid="devices-modal"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9998,
        background: 'rgba(0,0,0,0.72)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 12,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 680,
          maxHeight: '94vh',
          overflowY: 'auto',
          borderRadius: 16,
          background: colors.card || (dark ? '#141821' : '#ffffff'),
          color: colors.text || (dark ? '#f8fafc' : '#1e293b'),
          boxShadow: '0 20px 50px rgba(0,0,0,0.45)',
          padding: 18,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 19, fontWeight: 800 }}>
              {setupMode
                ? `🔌 ${tr('Scanner & Geräte einrichten (Setup)', 'Set Up Scanners & Devices (Setup)', 'Scanners & apparaten instellen (Setup)')}`
                : `📱 ${tr('Meine Geräte', 'My Devices', 'Mijn apparaten')}`}
            </h2>
            <div style={{ fontSize: 12.5, color: colors.textSecondary || '#64748b', marginTop: 2 }}>
              {setupMode
                ? tr(
                    'Richte WLAN-Lagerscanner (z. B. Zebra TC75), Bluetooth-Ringscanner oder Regal-Barcodes für dein Konto ein.',
                    'Set up Wi-Fi warehouse scanners (e.g. Zebra TC75), Bluetooth ring scanners or shelf barcodes for your account.',
                    'Stel wifi-magazijnscanners (bijv. Zebra TC75), Bluetooth-ringscanners of stelling-barcodes in.'
                  )
                : tr(
                    'Live-Übersicht aller Geräte in deinem Konto (unabhängig vom Standort, WLAN oder Mobilfunknetz).',
                    'Live overview of all devices in your account (regardless of location, Wi-Fi or mobile network).',
                    'Live-overzicht van alle apparaten in je account (ongeacht locatie, wifi of mobiel netwerk).'
                  )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <button
              type="button"
              onClick={() => loadDevices(false)}
              title={tr('Live-Status aktualisieren', 'Refresh live status', 'Live-status vernieuwen')}
              style={{
                padding: '7px 10px',
                borderRadius: 8,
                border: `1px solid ${dark ? '#334155' : '#cbd5e1'}`,
                background: 'transparent',
                color: colors.text || '#1e293b',
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              🔄
            </button>
            <button
              type="button"
              data-testid="devices-close-btn"
              onClick={onClose}
              style={{
                padding: '7px 12px',
                borderRadius: 8,
                border: 'none',
                background: dark ? '#334155' : '#e2e8f0',
                color: colors.text || '#1e293b',
                fontSize: 14,
                fontWeight: 800,
                cursor: 'pointer',
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {error && (
          <div style={{ padding: '10px 12px', borderRadius: 8, background: '#fee2e2', color: '#991b1b', fontSize: 13, fontWeight: 600 }}>
            ⚠️ {error}
          </div>
        )}

        {/* =====================================================================
            LIVE-ANSICHT ("📱 Meine Geräte")
           ===================================================================== */}
        {!setupMode && (
          <>
            {/* 1. 📱 HANDYS & TABLETS IM KONTO (LIVE) */}
            <div style={cardStyle} data-testid="my-devices-phones-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 6 }}>
                <strong style={{ fontSize: 15, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>📱</span>
                  <span>{tr('Handys & Tablets im Konto', 'Phones & Tablets in Account', 'Telefoons & tablets in account')}</span>
                </strong>
                <span style={{
                  fontSize: 11.5,
                  fontWeight: 700,
                  padding: '2px 9px',
                  borderRadius: 999,
                  background: dark ? '#064e3b' : '#dcfce7',
                  color: dark ? '#6ee7b7' : '#166534',
                }}>
                  🟢 {effectivePhones.filter((p) => p.isOnline).length} {tr('live aktiv', 'live online', 'live actief')}
                </span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {effectivePhones.map((ph) => {
                  const isMe = ph.clientId === myClientId;
                  const icon = ph.platform === 'pc' ? '💻' : ph.platform === 'tablet' ? '📟' : '📱';

                  // Welcher Drucker ist mit diesem Handy verbunden?
                  const myPrinterEntry = accountPrinters.find((pr) =>
                    pr.connectedPhones.some((cp) => cp.clientId === ph.clientId)
                  );
                  const otherPhonesOnSamePrinter = myPrinterEntry
                    ? myPrinterEntry.connectedPhones.filter((cp) => cp.clientId !== ph.clientId)
                    : [];

                  // Welche Scanner gehören zu diesem Handy?
                  const attachedBtScanners = devices.filter(
                    (d) => d.type === 'bt_scanner' && (d.config?.connectedPhoneClientId === ph.clientId || (!d.config?.connectedPhoneClientId && isMe))
                  );
                  const liveBondedBtScanners = Array.isArray(ph.bondedDevices)
                    ? ph.bondedDevices.filter((bd) =>
                        bd.connected &&
                        (!ph.printer?.address || String(bd.address || '').toUpperCase() !== String(ph.printer.address).toUpperCase())
                      )
                    : [];
                  const wlanScanners = devices.filter((d) => d.type === 'wlan_scanner');

                  return (
                    <div
                      key={ph.clientId || ph.id}
                      data-testid={`phone-card-${ph.clientId || ph.id}`}
                      style={{
                        padding: '12px 14px',
                        borderRadius: 11,
                        border: `1.5px solid ${isMe ? '#2563eb' : (ph.isOnline ? '#10b981' : (dark ? '#334155' : '#cbd5e1'))}`,
                        background: isMe
                          ? (dark ? 'rgba(37, 99, 235, 0.14)' : '#eff6ff')
                          : (dark ? '#141821' : '#ffffff'),
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                      }}
                    >
                      {/* Kopfzeile des Handys */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 18 }}>{icon}</span>
                          {editingPhoneId === (ph.clientId || ph.id) ? (
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                              <input
                                type="text"
                                value={editingPhoneName}
                                onChange={(e) => setEditingPhoneName(e.target.value)}
                                placeholder="z. B. Handy Vorne"
                                autoFocus
                                style={{
                                  padding: '4px 8px',
                                  borderRadius: 6,
                                  border: '1px solid #2563eb',
                                  background: dark ? '#0f172a' : '#fff',
                                  color: colors.text || '#1e293b',
                                  fontSize: 13.5,
                                  fontWeight: 700,
                                  width: 160,
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => handleSavePhoneName(ph)}
                                style={{
                                  padding: '4px 9px',
                                  borderRadius: 6,
                                  border: 'none',
                                  background: '#2563eb',
                                  color: '#fff',
                                  fontSize: 12,
                                  fontWeight: 700,
                                  cursor: 'pointer',
                                }}
                              >
                                ✓
                              </button>
                            </div>
                          ) : (
                            <>
                              <strong style={{ fontSize: 14.5 }}>{ph.name}</strong>
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingPhoneId(ph.clientId || ph.id);
                                  setEditingPhoneName(ph.name || '');
                                }}
                                title={tr('Handy-Namen ändern (z. B. Handy Vorne / Handy Lager)', 'Rename phone', 'Naam wijzigen')}
                                style={{
                                  border: 'none',
                                  background: 'transparent',
                                  color: colors.textSecondary || '#64748b',
                                  cursor: 'pointer',
                                  fontSize: 12,
                                  padding: '2px 4px',
                                }}
                              >
                                ✏️
                              </button>
                            </>
                          )}
                          {isMe && (
                            <span style={{
                              fontSize: 11,
                              fontWeight: 800,
                              padding: '2px 8px',
                              borderRadius: 999,
                              background: '#2563eb',
                              color: '#fff',
                            }}>
                              📍 {tr('Dieses Gerät', 'This Device', 'Dit apparaat')}
                            </span>
                          )}
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          {renderBatteryBadge(ph.battery)}
                          <span style={{
                            fontSize: 11.5,
                            fontWeight: 700,
                            padding: '2px 8px',
                            borderRadius: 999,
                            background: ph.isOnline ? (dark ? '#064e3b' : '#dcfce7') : (dark ? '#1e293b' : '#f1f5f9'),
                            color: ph.isOnline ? (dark ? '#6ee7b7' : '#166534') : (colors.textSecondary || '#64748b'),
                          }}>
                            {ph.isOnline
                              ? `🟢 ${tr('Live verbunden', 'Live online', 'Live verbonden')}`
                              : `⚪ ${formatLastSeen(ph.last_seen_at)}`}
                          </span>
                          {!isMe && ph.id && (
                            <button
                              type="button"
                              onClick={() => handleRemoveDevice(ph)}
                              title={tr('Altes Gerät aus Liste entfernen', 'Remove old device', 'Apparaat verwijderen')}
                              style={{
                                border: 'none',
                                background: 'transparent',
                                color: '#ef4444',
                                cursor: 'pointer',
                                fontSize: 12,
                                padding: '2px 4px',
                              }}
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Verbundene Geräte an diesem Handy */}
                      <div style={{
                        padding: '8px 10px',
                        borderRadius: 8,
                        background: dark ? '#0f172a' : '#f8fafc',
                        border: `1px solid ${dark ? '#1e293b' : '#e2e8f0'}`,
                        fontSize: 12.5,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 5,
                      }}>
                        {/* Drucker-Verbindung dieses Handys */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span>🖨️</span>
                          {myPrinterEntry ? (
                            <span>
                              <strong>{tr('Verbunden mit Drucker:', 'Connected to printer:', 'Verbonden met printer:')}</strong>{' '}
                              <span style={{ fontWeight: 700, color: dark ? '#6ee7b7' : '#047857' }}>
                                {myPrinterEntry.name}
                              </span>{' '}
                              <span style={{ opacity: 0.8 }}>
                                ({ph.printer?.remoteClient
                                  ? tr('Gleichzeitig über Druck-Brücke', 'Simultaneously via Print Bridge', 'Gelijktijdig via printbrug')
                                  : myPrinterEntry.mode === 'wifi'
                                    ? 'WLAN'
                                    : 'Bluetooth'})
                              </span>
                              {otherPhonesOnSamePrinter.length > 0 && (
                                <span style={{
                                  marginLeft: 6,
                                  padding: '1px 7px',
                                  borderRadius: 999,
                                  fontSize: 11,
                                  fontWeight: 700,
                                  background: dark ? '#1e3a8a' : '#dbeafe',
                                  color: dark ? '#93c5fd' : '#1e40af',
                                }}>
                                  🔗 {tr(
                                    `Gleichzeitig mit ${otherPhonesOnSamePrinter.map((p) => p.name).join(', ')}`,
                                    `Shared with ${otherPhonesOnSamePrinter.map((p) => p.name).join(', ')}`,
                                    `Gedeeld met ${otherPhonesOnSamePrinter.map((p) => p.name).join(', ')}`
                                  )}
                                </span>
                              )}
                            </span>
                          ) : (
                            <span style={{ opacity: 0.7 }}>
                              {tr('Kein Etikettendrucker verbunden', 'No label printer connected', 'Geen labelprinter verbonden')}
                            </span>
                          )}
                        </div>

                        {/* Scanner-Verbindung dieses Handys */}
                        {(attachedBtScanners.length > 0 || liveBondedBtScanners.length > 0 || wlanScanners.length > 0) && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <span>🔫</span>
                            <span>
                              <strong>{tr('Scanner:', 'Scanners:', 'Scanner:')}</strong>{' '}
                              {[
                                ...attachedBtScanners.map((s) => `💍 ${s.name} (Bluetooth)`),
                                ...liveBondedBtScanners
                                  .filter((bd) => !attachedBtScanners.some((s) => s.name === bd.name))
                                  .map((bd) => `💍 ${bd.name} (Bluetooth${Number.isFinite(Number(bd.batteryLevel)) ? ` · 🔋 ${bd.batteryLevel}%` : ''})`),
                                ...wlanScanners.map((s) => `📡 ${s.name} (WLAN-Sync)`),
                              ].join(' · ')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 2. 🖨️ ETIKETTENDRUCKER IM KONTO (INKL. GLEICHZEITIGER HANDY-VERBINDUNG) */}
            <div style={cardStyle} data-testid="my-devices-printers-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 6 }}>
                <strong style={{ fontSize: 15, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>🖨️</span>
                  <span>{tr('Etikettendrucker im Konto', 'Label Printers in Account', 'Labelprinters in account')}</span>
                </strong>
              </div>

              {accountPrinters.length === 0 ? (
                <div style={{ fontSize: 13, opacity: 0.78, padding: '4px 0' }}>
                  {tr(
                    'Aktuell ist auf keinem Handy ein Etikettendrucker verbunden. Das Einrichten erfolgt im Setup.',
                    'Currently no label printer is connected to any phone. Set up your printer in the Setup Wizard.',
                    'Momenteel is op geen enkele telefoon een labelprinter verbonden. Instellen gebeurt via de Setup.'
                  )}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {accountPrinters.map((pr) => {
                    const isMultiPhone = pr.connectedPhones.length >= 2;
                    const isMyPhoneConnected = pr.connectedPhones.some((cp) => cp.clientId === myClientId);
                    const myConnection = pr.connectedPhones.find((cp) => cp.clientId === myClientId);

                    return (
                      <div
                        key={pr.key}
                        data-testid={`account-printer-${pr.key}`}
                        style={{
                          padding: '12px 14px',
                          borderRadius: 11,
                          border: `1.5px solid ${isMultiPhone ? '#10b981' : (dark ? '#334155' : '#cbd5e1')}`,
                          background: dark ? '#141821' : '#ffffff',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 8,
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
                          <div>
                            <strong style={{ fontSize: 14.5, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                              <span>🖨️ {pr.name}</span>
                              <span style={{
                                fontSize: 11,
                                fontWeight: 700,
                                padding: '2px 7px',
                                borderRadius: 6,
                                background: dark ? '#1e293b' : '#e2e8f0',
                                color: dark ? '#93c5fd' : '#1e40af',
                              }}>
                                {pr.mode === 'wifi' ? `🌐 WLAN (${pr.address})` : '🔵 Bluetooth'} · {String(pr.lang || 'cpcl').toUpperCase()}
                              </span>
                              {renderBatteryBadge(pr.battery)}
                            </strong>
                          </div>

                          {isMyPhoneConnected && (
                            <button
                              type="button"
                              data-testid="my-devices-test-print"
                              disabled={testingPrint}
                              onClick={async () => {
                                setTestingPrint(true);
                                try {
                                  const addr = getPrinterConfig()?.address || pr.address;
                                  await testPrint(addr);
                                  if (window.showToast) window.showToast('🖨️ Testetikett gesendet!', 'success', 2500);
                                } catch (e) {
                                  setError(e?.message || 'Testdruck fehlgeschlagen');
                                } finally {
                                  setTestingPrint(false);
                                }
                              }}
                              style={{
                                padding: '5px 10px',
                                borderRadius: 7,
                                border: `1px solid ${dark ? '#475569' : '#cbd5e1'}`,
                                background: dark ? '#0f172a' : '#f8fafc',
                                color: colors.text || '#1e293b',
                                fontSize: 12,
                                fontWeight: 700,
                                cursor: 'pointer',
                              }}
                            >
                              {testingPrint ? '⏳ …' : `🖨️ ${tr('Testdruck', 'Test Print', 'Testafdruk')}`}
                            </button>
                          )}
                        </div>

                        {/* Welche Handys sind derzeit mit diesem Drucker verbunden? */}
                        <div style={{
                          padding: '8px 10px',
                          borderRadius: 8,
                          background: isMultiPhone
                            ? (dark ? 'rgba(16, 185, 129, 0.14)' : '#ecfdf5')
                            : (dark ? '#0f172a' : '#f8fafc'),
                          border: `1px solid ${isMultiPhone ? '#10b981' : (dark ? '#1e293b' : '#e2e8f0')}`,
                          fontSize: 12.5,
                        }}>
                          <div style={{ fontWeight: 700, marginBottom: 4, color: isMultiPhone ? (dark ? '#6ee7b7' : '#065f46') : 'inherit' }}>
                            {isMultiPhone
                              ? `🟢 ${tr(
                                  `Gleichzeitig mit ${pr.connectedPhones.length} Handys verbunden:`,
                                  `Simultaneously connected to ${pr.connectedPhones.length} phones:`,
                                  `Gelijktijdig verbonden met ${pr.connectedPhones.length} telefoons:`
                                )}`
                              : `🔗 ${tr('Derzeit verbunden mit Handy:', 'Currently connected to phone:', 'Momenteel verbonden met telefoon:')}`}
                          </div>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                            {pr.connectedPhones.map((cp) => (
                              <span
                                key={cp.clientId}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 5,
                                  padding: '3px 9px',
                                  borderRadius: 999,
                                  fontSize: 12,
                                  fontWeight: 700,
                                  background: cp.isCurrentDevice ? '#2563eb' : (dark ? '#1e293b' : '#e2e8f0'),
                                  color: cp.isCurrentDevice ? '#fff' : (colors.text || '#1e293b'),
                                }}
                              >
                                <span>{cp.isOnline ? '🟢' : '⚪'} 📱 {cp.name}</span>
                                {cp.connectionKind === 'bridge' && (
                                  <span style={{ opacity: 0.85, fontWeight: 500 }}>
                                    ({tr('Mitdrucken', 'Shared', 'Meedrukken')})
                                  </span>
                                )}
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* Falls dieses Handy noch NICHT mit diesem Konto-Drucker verbunden ist -> 1-Klick gleichzeitig verbinden */}
                        {!isMyPhoneConnected && (
                          <button
                            type="button"
                            data-testid="connect-second-phone-printer-btn"
                            onClick={() => handleConnectThisPhoneToPrinter(pr)}
                            style={{
                              padding: '8px 12px',
                              borderRadius: 8,
                              border: 'none',
                              background: '#10b981',
                              color: '#fff',
                              fontSize: 12.5,
                              fontWeight: 800,
                              cursor: 'pointer',
                              textAlign: 'left',
                            }}
                          >
                            🔗 {tr(
                              'Auch mit diesem Handy verbinden (beide Handys gleichzeitig mit dem Drucker nutzen)',
                              'Also connect this phone (use both phones simultaneously with this printer)',
                              'Ook met deze telefoon verbinden (beide telefoons tegelijk met deze printer gebruiken)'
                            )}
                          </button>
                        )}

                        {isMyPhoneConnected && myConnection?.connectionKind === 'bridge' && (
                          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                              type="button"
                              onClick={handleDisconnectThisPhoneFromBridge}
                              style={{
                                padding: '4px 9px',
                                borderRadius: 6,
                                border: `1px solid ${dark ? '#475569' : '#cbd5e1'}`,
                                background: 'transparent',
                                color: colors.textSecondary || '#64748b',
                                fontSize: 11.5,
                                cursor: 'pointer',
                              }}
                            >
                              {tr('Mitdrucken auf diesem Handy trennen', 'Disconnect shared printing on this phone', 'Meedrukken op deze telefoon verbreken')}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* 3. 📡 LAGER-SCANNER IM KONTO (ZEBRA TC75 & RINGSCANNER) */}
            <div style={cardStyle} data-testid="my-devices-scanners-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 6 }}>
                <strong style={{ fontSize: 15, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>📡</span>
                  <span>{tr('Scanner im Konto (Zebra TC75 & Ringscanner)', 'Scanners in Account (Zebra TC75 & Ring Scanners)', 'Scanners in account (Zebra TC75 & Ringscanners)')}</span>
                </strong>
                {devices.length > 0 && unbinnedCount > 0 && (
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: '#f59e0b' }}>
                    📦 {unbinnedCount} {tr('Pakete warten auf Regal-Scan', 'parcels waiting for shelf scan', 'pakketten wachten op vak-scan')}
                  </span>
                )}
              </div>

              {loading && devices.length === 0 ? (
                <div style={{ fontSize: 13, opacity: 0.7 }}>⏳ {tr('Lade Geräte …', 'Loading devices …', 'Apparaten laden …')}</div>
              ) : devices.length === 0 ? (
                <div style={{ fontSize: 13, opacity: 0.78, padding: '4px 0' }}>
                  {tr(
                    'Kein separater Lager-Scanner eingetragen (Einrichten erfolgt über das Setup).',
                    'No separate warehouse scanner registered (set up via the Setup Wizard).',
                    'Geen aparte magazijnscanner geregistreerd (instellen via de Setup).'
                  )}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {devices.map((dev) => {
                    const isSel = selectedDevice?.id === dev.id;
                    const isBt = dev.type === 'bt_scanner';
                    const connectedCid = dev.config?.connectedPhoneClientId || '';
                    const connectedPhone = effectivePhones.find((p) => p.clientId === connectedCid) || effectivePhones[0];
                    // Falls Bluetooth-Ringscanner: Akkustand aus gekoppelten BT-Geräten des verbundenen Handys prüfen
                    const btMatchOnPhone = isBt && connectedPhone && Array.isArray(connectedPhone.bondedDevices)
                      ? connectedPhone.bondedDevices.find((bd) =>
                          (dev.config?.btAddress && bd.address && String(bd.address).toUpperCase() === String(dev.config.btAddress).toUpperCase()) ||
                          (bd.connected && bd.address !== connectedPhone.printer?.address)
                        )
                      : null;
                    const scannerBattery = dev.battery || dev.config?.battery || (btMatchOnPhone && Number.isFinite(Number(btMatchOnPhone.batteryLevel))
                      ? { level: Number(btMatchOnPhone.batteryLevel) }
                      : null);

                    return (
                      <div
                        key={dev.id}
                        data-testid={`device-item-${dev.id}`}
                        style={{
                          padding: '10px 12px',
                          borderRadius: 10,
                          border: `1.5px solid ${dev.isOnline ? '#10b981' : (dark ? '#334155' : '#cbd5e1')}`,
                          background: dark ? '#141821' : '#ffffff',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 8,
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <div>
                            <strong style={{ fontSize: 14, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                              <span>{isBt ? '💍' : '📡'} {dev.name}</span>
                              {dev.config?.ip && (
                                <span style={{ fontSize: 11.5, fontFamily: 'monospace', fontWeight: 600, padding: '1px 6px', borderRadius: 5, background: dark ? '#1e293b' : '#e2e8f0', color: dark ? '#93c5fd' : '#1e40af' }}>
                                  🌐 {dev.config.ip}
                                </span>
                              )}
                              {renderBatteryBadge(scannerBattery)}
                            </strong>
                            <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>
                              {dev.last_seen_at
                                ? `${dev.isOnline ? '🟢 Live' : '🕒'} ${formatLastSeen(dev.last_seen_at)}`
                                : `⚪ ${tr('Bereit (noch kein Scan)', 'Ready (no scan yet)', 'Gereed (nog geen scan)')}`}
                              {dev.last_scan ? ` · Letzter Scan: ${typeof dev.last_scan === 'object' ? dev.last_scan.code : dev.last_scan}` : ''}
                            </div>
                          </div>

                          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                            {!isBt && (
                              <button
                                type="button"
                                onClick={() => setSelectedDevice(isSel ? null : dev)}
                                style={{
                                  padding: '5px 9px',
                                  borderRadius: 6,
                                  border: `1px solid ${dark ? '#475569' : '#cbd5e1'}`,
                                  background: isSel ? '#2563eb' : 'transparent',
                                  color: isSel ? '#fff' : (colors.text || '#1e293b'),
                                  fontSize: 11.5,
                                  fontWeight: 700,
                                  cursor: 'pointer',
                                }}
                              >
                                🔗 {tr('QR / Link', 'QR / Link', 'QR / Link')}
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleRemoveDevice(dev)}
                              style={{
                                padding: '5px 8px',
                                borderRadius: 6,
                                border: `1px solid ${dark ? '#475569' : '#cbd5e1'}`,
                                background: 'transparent',
                                color: '#ef4444',
                                fontSize: 12,
                                fontWeight: 700,
                                cursor: 'pointer',
                              }}
                            >
                              🗑️
                            </button>
                          </div>
                        </div>

                        {/* Zuordnung zum Handy + Aufgabe */}
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', fontSize: 12 }}>
                          {isBt ? (
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ fontWeight: 700 }}>🔗 {tr('Verbunden mit Handy:', 'Connected to phone:', 'Verbonden met telefoon:')}</span>
                              <select
                                value={connectedCid || myClientId}
                                onChange={(e) => handleAssignScannerToPhone(dev, e.target.value)}
                                style={{
                                  padding: '4px 8px',
                                  borderRadius: 6,
                                  border: `1px solid ${dark ? '#475569' : '#cbd5e1'}`,
                                  background: dark ? '#0f172a' : '#f8fafc',
                                  color: colors.text || '#1e293b',
                                  fontSize: 12,
                                  fontWeight: 600,
                                }}
                              >
                                {effectivePhones.map((p) => (
                                  <option key={p.clientId} value={p.clientId}>
                                    📱 {p.name}{p.clientId === myClientId ? ' (Dieses Gerät)' : ''}
                                  </option>
                                ))}
                              </select>
                            </label>
                          ) : (
                            <span style={{
                              padding: '3px 8px',
                              borderRadius: 6,
                              background: dark ? '#0f172a' : '#f1f5f9',
                              fontWeight: 600,
                            }}>
                              🌐 {tr('Eigenständig über WLAN — live mit allen Handys im Konto verbunden', 'Standalone via Wi-Fi — live synced with all phones in account', 'Zelfstandig via wifi — live verbonden met alle telefoons')}
                            </span>
                          )}

                          <select
                            data-testid={`device-role-${dev.id}`}
                            value={dev.role || 'lager_einlagern'}
                            onChange={(e) => handleRoleChange(dev, e.target.value)}
                            style={{
                              marginLeft: 'auto',
                              padding: '4px 8px',
                              borderRadius: 6,
                              border: `1px solid ${dark ? '#475569' : '#cbd5e1'}`,
                              background: dark ? '#0f172a' : '#f8fafc',
                              color: colors.text || '#1e293b',
                              fontSize: 12,
                              fontWeight: 600,
                              maxWidth: 260,
                            }}
                          >
                            {DEVICE_ROLES.map((r) => (
                              <option key={r.id} value={r.id}>
                                {language === 'nl' ? r.labelNl : language === 'en' ? r.labelEn : r.labelDe}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Optional aufgeklappter QR-Code für Zebra TC75 */}
              {selectedDevice && (
                <div
                  data-testid="device-pairing-box"
                  style={{
                    marginTop: 10,
                    padding: 12,
                    borderRadius: 10,
                    border: `1px dashed ${dark ? '#475569' : '#94a3b8'}`,
                    background: dark ? '#0f172a' : '#ffffff',
                    display: 'flex',
                    gap: 14,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                  }}
                >
                  {qrDataUrl && (
                    <div style={{ textAlign: 'center' }}>
                      <img
                        src={qrDataUrl}
                        alt="Scanner QR"
                        style={{ width: 125, height: 125, borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff' }}
                      />
                      <div style={{ fontSize: 11, opacity: 0.75, marginTop: 3 }}>
                        Mit {selectedDevice.name} scannen
                      </div>
                    </div>
                  )}
                  <div style={{ flex: '1 1 220px', fontSize: 12.5, lineHeight: 1.5 }}>
                    <strong style={{ fontSize: 13.5, display: 'block', marginBottom: 4 }}>
                      🔗 {selectedDevice.name} Direktlink:
                    </strong>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                      <input
                        type="text"
                        readOnly
                        value={`${apiBase}/devices/terminal/${selectedDevice.token}`}
                        style={{
                          flex: 1,
                          padding: '5px 8px',
                          fontSize: 11.5,
                          fontFamily: 'monospace',
                          borderRadius: 6,
                          border: `1px solid ${dark ? '#334155' : '#cbd5e1'}`,
                          background: dark ? '#1e293b' : '#f1f5f9',
                          color: colors.text || '#1e293b',
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => copyText('term', `${apiBase}/devices/terminal/${selectedDevice.token}`)}
                        style={{
                          padding: '5px 10px',
                          borderRadius: 6,
                          border: 'none',
                          background: '#2563eb',
                          color: '#fff',
                          fontSize: 11.5,
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        {copiedKey === 'term' ? '✅' : 'Kopieren'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* 4. HINWEIS & BUTTON ZUM SETUP (KEIN DOPPELTES EINRICHTUNGS-FORMULAR UNTERHALB) */}
            <div
              style={{
                padding: '12px 16px',
                borderRadius: 12,
                border: `1px dashed ${dark ? '#475569' : '#94a3b8'}`,
                background: dark ? 'rgba(30, 41, 59, 0.5)' : '#f1f5f9',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 10,
              }}
            >
              <div style={{ flex: '1 1 240px', fontSize: 12.5, lineHeight: 1.45 }}>
                <strong style={{ display: 'block', fontSize: 13.5 }}>
                  🧭 {tr('Neuen Drucker oder Scanner einrichten?', 'Set up a new printer or scanner?', 'Nieuwe printer of scanner instellen?')}
                </strong>
                <span style={{ opacity: 0.85 }}>
                  {tr(
                    'Das Einrichten und Koppeln von Etikettendruckern, WLAN-Scannern und Regal-Barcodes erfolgt zentral über das Setup.',
                    'Setting up and pairing label printers, Wi-Fi scanners and shelf barcodes is done centrally in the Setup Wizard.',
                    'Het instellen en koppelen van labelprinters, wifi-scanners en stelling-barcodes gebeurt centraal via de Setup.'
                  )}
                </span>
              </div>
              {onOpenSetup && (
                <button
                  type="button"
                  data-testid="devices-jump-to-setup"
                  onClick={onOpenSetup}
                  style={{
                    padding: '9px 14px',
                    borderRadius: 9,
                    border: 'none',
                    background: '#1976d2',
                    color: '#fff',
                    fontSize: 13,
                    fontWeight: 800,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  🧭 {tr('Im Setup einrichten →', 'Open in Setup →', 'In Setup instellen →')}
                </button>
              )}
            </div>
          </>
        )}

        {/* =====================================================================
            SETUP-MODUS (`setupMode = true`, geöffnet aus Setup Schritt 4)
           ===================================================================== */}
        {setupMode && (
          <>
            {/* Hintergrund-Einlagerung & Regal-Barcodes */}
            <div
              style={{
                padding: '12px 16px',
                borderRadius: 12,
                border: `1.5px solid ${dark ? '#10b981' : '#6ee7b7'}`,
                background: dark ? '#0f291e' : '#ecfdf5',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 10,
              }}
            >
              <div style={{ flex: '1 1 260px' }}>
                <strong style={{ fontSize: 14, display: 'block', color: dark ? '#6ee7b7' : '#065f46' }}>
                  ✅ {tr('Lager-Scans laufen automatisch still im Hintergrund', 'Warehouse scans run silently in the background', 'Magazijnscans draaien stil op de achtergrond')}
                </strong>
                <span style={{ fontSize: 12.5, opacity: 0.92 }}>
                  {tr(
                    'Sobald ein gekoppelter Scanner (Zebra TC75 per WLAN oder Ringscanner per Bluetooth) Paket + Regal scannt, wird der Regalplatz automatisch gespeichert.',
                    'Whenever a paired scanner scans parcel + shelf, the shelf slot is automatically saved in the background.',
                    'Zodra een gekoppelde scanner pakket + vak scant, wordt het vak automatisch opgeslagen.'
                  )}
                </span>
              </div>
              <button
                type="button"
                data-testid="devices-open-shelf-codes"
                onClick={() => setShowShelfCodes(true)}
                style={{
                  padding: '8px 13px',
                  borderRadius: 9,
                  border: 'none',
                  background: '#16a34a',
                  color: '#fff',
                  fontSize: 12.5,
                  fontWeight: 800,
                  cursor: 'pointer',
                }}
              >
                🏷️ {tr('Regal-Barcodes drucken', 'Print Shelf Barcodes', 'Stelling-barcodes printen')}
              </button>
            </div>

            {/* Scanner anlegen & koppeln */}
            <div style={cardStyle}>
              <strong style={{ fontSize: 14.5, display: 'block', marginBottom: 4 }}>
                ➕ {tr('WLAN-Scanner (z. B. Zebra TC75) oder Ringscanner anlegen', 'Add Wi-Fi Scanner (e.g. Zebra TC75) or Ring Scanner', 'Wifi-scanner (bijv. Zebra TC75) of ringscanner toevoegen')}
              </strong>
              <div style={{ fontSize: 12.5, color: colors.textSecondary || '#64748b', marginBottom: 10, lineHeight: 1.45 }}>
                {tr(
                  'Lege deinen Zebra TC75 oder Ringscanner hier fürs Konto an. Für den Zebra TC75 erhältst du sofort einen QR-Code & Direktlink fürs blinde Einlagern über WLAN.',
                  'Register your Zebra TC75 or ring scanner here for your account. For the Zebra TC75 you immediately get a QR code & direct link.',
                  'Registreer hier je Zebra TC75 of ringscanner voor je account.'
                )}
              </div>

              <form onSubmit={handleAddDevice} style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                <input
                  type="text"
                  data-testid="devices-new-name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="z. B. Zebra TC75 Hinterlager"
                  style={{
                    flex: '1 1 180px',
                    padding: '8px 10px',
                    borderRadius: 8,
                    border: `1px solid ${dark ? '#475569' : '#cbd5e1'}`,
                    background: dark ? '#0f172a' : '#fff',
                    color: colors.text || '#1e293b',
                    fontSize: 13.5,
                  }}
                />
                <select
                  data-testid="devices-new-type"
                  value={newType}
                  onChange={(e) => {
                    const v = e.target.value;
                    setNewType(v);
                    if (v === 'bt_scanner' && newName.startsWith('Zebra')) setNewName('Bluetooth Ringscanner');
                    if (v === 'wlan_scanner' && newName.startsWith('Bluetooth')) setNewName('Zebra TC75 Lager');
                  }}
                  style={{
                    padding: '8px 10px',
                    borderRadius: 8,
                    border: `1px solid ${dark ? '#475569' : '#cbd5e1'}`,
                    background: dark ? '#0f172a' : '#fff',
                    color: colors.text || '#1e293b',
                    fontSize: 13,
                  }}
                >
                  <option value="wlan_scanner">📡 WLAN-Scanner (Zebra TC75 / MDE)</option>
                  <option value="bt_scanner">💍 Bluetooth / 2.4GHz Ringscanner</option>
                </select>
                <select
                  data-testid="devices-new-role"
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  style={{
                    flex: '1 1 220px',
                    padding: '8px 10px',
                    borderRadius: 8,
                    border: `1px solid ${dark ? '#475569' : '#cbd5e1'}`,
                    background: dark ? '#0f172a' : '#fff',
                    color: colors.text || '#1e293b',
                    fontSize: 12.5,
                  }}
                >
                  {DEVICE_ROLES.map((r) => (
                    <option key={r.id} value={r.id}>
                      {language === 'nl' ? r.labelNl : language === 'en' ? r.labelEn : r.labelDe}
                    </option>
                  ))}
                </select>
                {newType === 'wlan_scanner' && (
                  <div style={{ display: 'flex', gap: 6, flex: '1 1 260px', alignItems: 'center' }}>
                    <input
                      type="text"
                      data-testid="devices-new-ip"
                      value={newIp}
                      onChange={(e) => setNewIp(e.target.value)}
                      placeholder="IP-Adresse (optional, z. B. 192.168.178.45)"
                      style={{
                        flex: 1,
                        padding: '8px 10px',
                        borderRadius: 8,
                        border: `1px solid ${dark ? '#475569' : '#cbd5e1'}`,
                        background: dark ? '#0f172a' : '#fff',
                        color: colors.text || '#1e293b',
                        fontSize: 12.5,
                        fontFamily: 'monospace',
                      }}
                    />
                    <button
                      type="button"
                      data-testid="devices-ip-search-btn"
                      onClick={() => {
                        const nextShow = !showIpScanner;
                        setShowIpScanner(nextShow);
                        if (nextShow && foundIps.length === 0 && !ipScanning) {
                          runSubnetIpScan(subnetPrefix);
                        }
                      }}
                      style={{
                        padding: '8px 10px',
                        borderRadius: 8,
                        border: `1px solid ${dark ? '#3b82f6' : '#60a5fa'}`,
                        background: showIpScanner ? '#2563eb' : (dark ? '#1e3a8a' : '#eff6ff'),
                        color: showIpScanner ? '#fff' : (dark ? '#93c5fd' : '#1d4ed8'),
                        fontSize: 12.5,
                        fontWeight: 700,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      🔍 {tr('IP-Suche', 'IP Search', 'IP-zoeken')}
                    </button>
                  </div>
                )}
                <button
                  type="submit"
                  data-testid="devices-add-btn"
                  disabled={creating || !newName.trim()}
                  style={{
                    padding: '8px 14px',
                    borderRadius: 8,
                    border: 'none',
                    background: '#10b981',
                    color: '#fff',
                    fontSize: 13,
                    fontWeight: 800,
                    cursor: 'pointer',
                  }}
                >
                  + {tr('Gerät anlegen', 'Add Device', 'Apparaat toevoegen')}
                </button>
              </form>

              {/* 🔍 Aufklappbarer WLAN-IP-Scanner */}
              {showIpScanner && (
                <div
                  data-testid="devices-ip-scanner-box"
                  style={{
                    marginBottom: 12,
                    padding: 12,
                    borderRadius: 10,
                    border: `1px solid ${dark ? '#3b82f6' : '#93c5fd'}`,
                    background: dark ? 'rgba(30, 58, 138, 0.2)' : '#eff6ff',
                    fontSize: 12.5,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                    <strong>🔍 {tr('WLAN nach IP-Geräten durchsuchen (Scanner & Drucker)', 'Scan Wi-Fi for IP Devices (Scanners & Printers)', 'Wifi scannen op IP-apparaten')}</strong>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {['192.168.178', '192.168.1', '192.168.0', '192.168.2'].map((pre) => (
                        <button
                          key={pre}
                          type="button"
                          onClick={() => {
                            setSubnetPrefix(pre);
                            runSubnetIpScan(pre);
                          }}
                          style={{
                            padding: '3px 8px',
                            borderRadius: 6,
                            fontSize: 11,
                            fontWeight: 700,
                            fontFamily: 'monospace',
                            border: `1px solid ${subnetPrefix === pre ? '#2563eb' : (dark ? '#475569' : '#cbd5e1')}`,
                            background: subnetPrefix === pre ? '#2563eb' : (dark ? '#0f172a' : '#fff'),
                            color: subnetPrefix === pre ? '#fff' : (colors.text || '#1e293b'),
                            cursor: 'pointer',
                          }}
                        >
                          {pre}.x
                        </button>
                      ))}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8 }}>
                    <input
                      type="text"
                      value={subnetPrefix}
                      onChange={(e) => setSubnetPrefix(e.target.value)}
                      placeholder="192.168.178"
                      style={{
                        width: 130,
                        padding: '6px 9px',
                        borderRadius: 6,
                        border: `1px solid ${dark ? '#475569' : '#cbd5e1'}`,
                        background: dark ? '#0f172a' : '#fff',
                        color: colors.text || '#1e293b',
                        fontFamily: 'monospace',
                        fontSize: 12.5,
                      }}
                    />
                    <span style={{ fontFamily: 'monospace', opacity: 0.75 }}>.1 – .254</span>
                    <button
                      type="button"
                      onClick={() => runSubnetIpScan(subnetPrefix)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: 6,
                        border: 'none',
                        background: ipScanning ? '#ef4444' : '#2563eb',
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: 12,
                        cursor: 'pointer',
                      }}
                    >
                      {ipScanning ? `⏹️ Stopp (${ipScanProgress}%)` : '🔄 Jetzt suchen'}
                    </button>
                  </div>

                  {ipScanning && (
                    <div style={{ height: 6, borderRadius: 999, background: dark ? '#1e293b' : '#dbeafe', overflow: 'hidden', marginBottom: 8 }}>
                      <div style={{ width: `${ipScanProgress}%`, height: '100%', background: '#2563eb', transition: 'width 0.2s' }} />
                    </div>
                  )}

                  {foundIps.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, maxHeight: 170, overflowY: 'auto' }}>
                      {foundIps.map((item) => (
                        <div
                          key={item.ip}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 8,
                            flexWrap: 'wrap',
                            padding: '6px 9px',
                            borderRadius: 7,
                            background: dark ? '#0f172a' : '#ffffff',
                            border: `1px solid ${item.kind === 'printer' ? '#10b981' : (dark ? '#334155' : '#cbd5e1')}`,
                          }}
                        >
                          <div>
                            <strong style={{ fontFamily: 'monospace', fontSize: 13 }}>{item.ip}</strong>
                            <span style={{ marginLeft: 8, fontSize: 11.5, opacity: 0.85 }}>{item.label} ({item.ms}ms)</span>
                          </div>
                          <div style={{ display: 'flex', gap: 5 }}>
                            <button
                              type="button"
                              onClick={() => {
                                setNewIp(item.ip);
                                setShowIpScanner(false);
                              }}
                              style={{
                                padding: '4px 9px',
                                borderRadius: 6,
                                border: 'none',
                                background: '#2563eb',
                                color: '#fff',
                                fontSize: 11.5,
                                fontWeight: 700,
                                cursor: 'pointer',
                              }}
                            >
                              ✓ IP übernehmen
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setPrinterConfig({
                                  name: `WLAN-Drucker (${item.ip})`,
                                  address: `${item.ip}:9100`,
                                  type: 'tcp',
                                });
                                setPrinterLang('zpl');
                                setAutoPrintEnabled(true);
                                if (window.showToast) {
                                  window.showToast(`🖨️ WLAN-Drucker ${item.ip}:9100 gekoppelt!`, 'success', 3000);
                                }
                              }}
                              style={{
                                padding: '4px 9px',
                                borderRadius: 6,
                                border: '1px solid #10b981',
                                background: dark ? 'rgba(16,185,129,0.15)' : '#ecfdf5',
                                color: dark ? '#6ee7b7' : '#047857',
                                fontSize: 11.5,
                                fontWeight: 700,
                                cursor: 'pointer',
                              }}
                            >
                              🖨️ Als Drucker
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Angelegte Scanner + Kopplungs-QR */}
              {devices.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {devices.map((dev) => {
                    const isSel = selectedDevice?.id === dev.id;
                    return (
                      <div
                        key={dev.id}
                        onClick={() => setSelectedDevice(dev)}
                        style={{
                          padding: '10px 12px',
                          borderRadius: 10,
                          cursor: 'pointer',
                          border: `1.5px solid ${isSel ? '#2563eb' : (dark ? '#334155' : '#cbd5e1')}`,
                          background: isSel ? (dark ? '#172554' : '#eff6ff') : (dark ? '#141821' : '#ffffff'),
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 8,
                          flexWrap: 'wrap',
                        }}
                      >
                        <strong>{dev.type === 'bt_scanner' ? '💍' : '📡'} {dev.name}</strong>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveDevice(dev);
                          }}
                          style={{
                            padding: '4px 8px',
                            borderRadius: 6,
                            border: `1px solid ${dark ? '#475569' : '#cbd5e1'}`,
                            background: 'transparent',
                            color: '#ef4444',
                            fontSize: 12,
                            cursor: 'pointer',
                          }}
                        >
                          🗑️
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {selectedDevice && (
                <div
                  data-testid="device-pairing-box"
                  style={{
                    marginTop: 12,
                    padding: 12,
                    borderRadius: 10,
                    border: `1px dashed ${dark ? '#475569' : '#94a3b8'}`,
                    background: dark ? '#0f172a' : '#ffffff',
                    display: 'flex',
                    gap: 14,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                  }}
                >
                  {qrDataUrl && (
                    <div style={{ textAlign: 'center' }}>
                      <img
                        src={qrDataUrl}
                        alt="Scanner QR"
                        style={{ width: 135, height: 135, borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff' }}
                      />
                      <div style={{ fontSize: 11, opacity: 0.75, marginTop: 3 }}>
                        Mit Zebra TC75 scannen
                      </div>
                    </div>
                  )}
                  <div style={{ flex: '1 1 240px', fontSize: 12.5, lineHeight: 1.5 }}>
                    <strong style={{ fontSize: 13.5, display: 'block', marginBottom: 4 }}>
                      🔗 {selectedDevice.name} verbinden:
                    </strong>
                    <div style={{ marginBottom: 6 }}>
                      Öffne diesen Link auf dem TC75 (oder scanne den QR-Code):
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input
                        type="text"
                        readOnly
                        value={`${apiBase}/devices/terminal/${selectedDevice.token}`}
                        style={{
                          flex: 1,
                          padding: '5px 8px',
                          fontSize: 11.5,
                          fontFamily: 'monospace',
                          borderRadius: 6,
                          border: `1px solid ${dark ? '#334155' : '#cbd5e1'}`,
                          background: dark ? '#1e293b' : '#f1f5f9',
                          color: colors.text || '#1e293b',
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => copyText('term', `${apiBase}/devices/terminal/${selectedDevice.token}`)}
                        style={{
                          padding: '5px 10px',
                          borderRadius: 6,
                          border: 'none',
                          background: '#2563eb',
                          color: '#fff',
                          fontSize: 11.5,
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        {copiedKey === 'term' ? '✅ Kopiert' : 'Kopieren'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Scanner-Testfeld */}
            <div style={cardStyle}>
              <strong style={{ fontSize: 14, display: 'block', marginBottom: 4 }}>
                🧪 {tr('Scanner-Testfeld (Ringscanner / Zebra TC75 prüfen)', 'Scanner Test Box (Verify Ring Scanner / Zebra TC75)', 'Scanner-testveld')}
              </strong>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!testInput.trim()) return;
                  unlockWarehouseAudio();
                  playWarehouseTone('stored');
                  const ms = testStartRef.current ? Math.max(1, Date.now() - testStartRef.current) : 0;
                  setLastTestScan({ code: testInput.trim(), ms, enterOk: true, at: new Date().toLocaleTimeString() });
                  setTestInput('');
                  testStartRef.current = 0;
                }}
                style={{ display: 'flex', gap: 8, marginTop: 6 }}
              >
                <input
                  ref={testInputRef}
                  type="text"
                  data-testid="devices-scanner-test-input"
                  value={testInput}
                  onChange={(e) => {
                    if (!testStartRef.current) testStartRef.current = Date.now();
                    setTestInput(e.target.value);
                  }}
                  placeholder={tr('Hier klicken & Barcode scannen …', 'Click here & scan a barcode …', 'Klik hier & scan een barcode …')}
                  style={{
                    flex: 1,
                    padding: '9px 12px',
                    borderRadius: 8,
                    border: `1.5px solid ${dark ? '#475569' : '#94a3b8'}`,
                    background: dark ? '#0f172a' : '#fff',
                    color: colors.text || '#1e293b',
                    fontSize: 14,
                  }}
                />
              </form>
              {lastTestScan && (
                <div
                  data-testid="devices-scanner-test-result"
                  style={{
                    marginTop: 8,
                    padding: '8px 10px',
                    borderRadius: 8,
                    background: dark ? '#064e3b' : '#d1fae5',
                    color: dark ? '#ecfdf5' : '#065f46',
                    fontSize: 12.5,
                    fontWeight: 600,
                  }}
                >
                  ✅ {tr('Erfolgreich gelesen:', 'Successfully read:', 'Succesvol gelezen:')} <code>{lastTestScan.code}</code> ({lastTestScan.code.length} Zeichen in {lastTestScan.ms} ms · Enter-Suffix OK)
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {showShelfCodes && (
        <ShelfCodesModal
          onClose={() => setShowShelfCodes(false)}
          colors={colors}
          language={language}
        />
      )}
    </div>
  );
}
